import api from './client';
import type {
  Portfolio,
  Order,
  DailyPnl,
  CncEligiblePosition,
  ChargesReport,
  KiteHolding,
  ClosedPosition,
  SyncStatus,
  FeedStatus,
  AutoTradeStatus,
  CrashProtectionStatus,
} from './types';

// ── Portfolio ────────────────────────────────────────────────────────────────

export const fetchPortfolio = () =>
  api.get<Portfolio>("/portfolio/").then((r) => r.data);

export const syncPortfolioFromKite = () =>
  api.post<{ status: string; cash_balance: number; orders_deleted: number; orders_imported: number; positions_synced: number; message: string }>(
    "/portfolio/sync-from-kite"
  ).then((r) => r.data);

export const fetchPnlHistory = () =>
  api.get<DailyPnl[]>("/portfolio/pnl-history").then((r) => r.data);

export const resetAllData = () =>
  api.post<{ status: string; message: string; capital: number }>("/portfolio/reset").then((r) => r.data);

// ── Orders ───────────────────────────────────────────────────────────────────

// tradeDate: "today" (default — current day only), "all" (full history), or "YYYY-MM-DD"
export const fetchOrders = (limit = 50, tradeDate = "today") =>
  api.get<Order[]>(`/orders/?limit=${limit}&trade_date=${tradeDate}`).then((r) => r.data);

export const placeOrder = (order: {
  symbol: string;
  side: "BUY" | "SELL";
  quantity: number;
  price: number;
  strategy?: string;
}) => api.post("/orders/", order).then((r) => r.data);

export const closePosition = (symbol: string, quantity: number, ltp: number, side: "BUY" | "SELL", instrument_type = "EQUITY") =>
  api.post("/orders/", { symbol, side, quantity, price: ltp, strategy: "manual_close", instrument_type }).then((r) => r.data);

export const overridePositionSL = (symbol: string, slPrice: number) =>
  api.patch<{ status: string; symbol: string; sl_price: number }>(
    `/portfolio/positions/${encodeURIComponent(symbol)}/sl`,
    { sl_price: slPrice },
  ).then((r) => r.data);

// ── Market data ──────────────────────────────────────────────────────────────

export const fetchBulkLtp = (symbols: string[]) =>
  api.get<Record<string, number>>(`/market/ltp?symbols=${symbols.join(",")}`).then((r) => r.data);

// ── MIS to CNC conversion ───────────────────────────────────────────────────

export const fetchCncEligible = () =>
  api.get<{
    eligible: CncEligiblePosition[];
    ineligible: CncEligiblePosition[];
    cnc_margin_available: number;
  }>("/portfolio/cnc-eligible").then((r) => r.data);

export const convertToCnc = (symbol: string, quantity: number, side: "BUY" | "SELL") =>
  api.post<{
    status: string;
    symbol: string;
    quantity: number;
    pnl_pct: number;
    cnc_margin_used: number;
    message: string;
  }>("/portfolio/convert-to-cnc", { symbol, quantity, side }).then((r) => r.data);

export const convertToDelivery = (symbol: string, quantity: number, transaction_type: "BUY" | "SELL") =>
  api.post<{ status: string; symbol: string; quantity: number; product: string; message: string }>(
    "/portfolio/convert-to-delivery",
    { symbol, quantity, transaction_type },
  ).then((r) => r.data);

// ── Charges ──────────────────────────────────────────────────────────────────

export const fetchChargesReport = (tradeDate = "today") =>
  api.get<ChargesReport>(`/portfolio/charges?trade_date=${tradeDate}`).then((r) => r.data);

// ── Paper Trading ───────────────────────────────────────────────────────────

export const fetchPaperPositions = (date?: string) =>
  api.get(`/portfolio/paper/positions${date ? `?date=${date}` : ""}`).then((r) => r.data);

export const fetchPaperOrders = (limit = 100, date?: string) =>
  api.get(`/portfolio/paper/orders?limit=${limit}${date ? `&date=${date}` : ""}`).then((r) => r.data);

export const fetchPaperPnl = () =>
  api.get("/portfolio/paper/pnl").then((r) => r.data);

export const fetchPaperDaily = () =>
  api.get("/portfolio/paper/daily").then((r) => r.data);

// ── Trades Explorer (rebuilt P&L page) ──────────────────────────────────────

export interface TradesExplorerSummary {
  gross_pnl: number; brokerage: number; net_pnl: number;
  trades: number; wins: number; losses: number; win_rate_pct: number;
  biggest_win_inr: number; biggest_win_sym: string | null;
  biggest_loss_inr: number; biggest_loss_sym: string | null;
  avg_holding_secs: number;
}
export interface TradesExplorerStratRow {
  strategy: string; trades: number; wins: number; losses: number;
  gross: number; net: number;
}
export interface TradesExplorerCard {
  symbol: string; side: "BUY" | "SELL"; quantity: number;
  entry_price: number; exit_price: number;
  gross_pnl: number; brokerage: number; net_pnl: number;
  entry_strategy: string; exit_strategy: string; exit_reason: string;
  entry_time: string | null; exit_time: string | null; holding_secs: number;
  instrument_type: string; lot_size: number;
  entry_order_id: string; exit_order_id: string;
  entry_indicators: {
    ts: string | null; strategy: string; signal: string;
    rsi: number | null; macd: number | null; atr: number | null;
    score: number | null; sl_price: number | null; tp1_price: number | null;
    signal_price: number | null; reason: string;
  } | null;
  exit_event: { type: string; reason: string; ts: string; data: Record<string, unknown> } | null;
  // Predictor snapshot at the moment of close — captured from the Prediction
  // object and PositionState. Powers the "What the predictor was saying" panel.
  exit_predictor: {
    direction?: string | null;
    confidence?: number | null;
    momentum_pct?: number | null;
    ema5?: number | null;
    ema20?: number | null;
    pnl_pct?: number | null;
    trailing_stop?: number | null;
    trail_phase?: string | null;
    signal?: string | null;
    peak_price?: number | null;
    peak_pnl?: number | null;
    breakeven_locked?: boolean | null;
    last_rsi?: number | null;
    tick_count?: number | null;
    atr?: number | null;
  } | null;
}
export interface TradesExplorerResponse {
  date: string; mode: "paper" | "live";
  summary: TradesExplorerSummary;
  by_strategy: TradesExplorerStratRow[];
  trades: TradesExplorerCard[];
  recent_days: { date: string; gross: number; trades: number }[];
}

export const fetchTradesExplorer = (mode: "paper" | "live", date?: string) =>
  api.get<TradesExplorerResponse>(
    `/portfolio/trades-explorer?mode=${mode}${date ? `&date=${date}` : ""}`
  ).then(r => r.data);

// ── Holdings ─────────────────────────────────────────────────────────────────

export const fetchClosedPositions = () =>
  api.get<ClosedPosition[]>("/portfolio/closed-positions").then((r) => r.data);

export const fetchHoldings = () =>
  api.get<KiteHolding[]>("/portfolio/holdings").then((r) => r.data);

// ── Candle store + live feed ─────────────────────────────────────────────────

export const triggerCandleSync  = () => api.post<SyncStatus>("/candles/sync").then(r => r.data);
export const fetchSyncStatus    = () => api.get<SyncStatus>("/candles/sync-status").then(r => r.data);
export const startMarketFeed    = () => api.post<{ message: string }>("/candles/start-feed").then(r => r.data);
export const stopMarketFeed     = () => api.post<FeedStatus>("/candles/stop-feed").then(r => r.data);
export const fetchFeedStatus    = () => api.get<FeedStatus>("/candles/feed-status").then(r => r.data);
export const triggerCandleBackfill = (from_date: string, to_date: string) =>
  api.post<{ message: string; running: boolean; phase: string; candles_saved: number }>("/candles/backfill", { from_date, to_date }).then(r => r.data);
export const fetchBackfillStatus = () =>
  api.get<{ running: boolean; phase: string; from_date: string | null; to_date: string | null; symbols_total: number; symbols_done: number; candles_saved: number; errors: number }>("/candles/backfill-status").then(r => r.data);

// ── Auto-trade ───────────────────────────────────────────────────────────────

export const fetchAutoTradeStatus = () =>
  api.get<AutoTradeStatus>("/signals/auto-trade/status").then((r) => r.data);

export const toggleAutoTrade = () =>
  api.post<{ enabled: boolean }>("/signals/auto-trade/toggle").then((r) => r.data);

export const fetchCrashProtectionStatus = () =>
  api.get<CrashProtectionStatus>("/signals/crash-protection/status").then((r) => r.data);

export const toggleCrashProtection = () =>
  api.post<CrashProtectionStatus>("/signals/crash-protection/toggle").then((r) => r.data);

// ── Auth ─────────────────────────────────────────────────────────────────────

export const fetchAuthStatus = () =>
  api.get<{ authenticated: boolean }>("/auth/status").then((r) => r.data);

export const fetchLoginUrl = () =>
  api.get<{ url: string }>("/auth/login-url").then((r) => r.data);

export const createSession = (request_token: string) =>
  api.post("/auth/session", { request_token }).then((r) => r.data);

export const kiteLogout = () =>
  api.post<{ status: string }>("/auth/logout").then((r) => r.data);

// ── Trade Journal ────────────────────────────────────────────────────────────

import type { TradeSession, TradeEventLog, TradeChartData } from './types';

export const fetchTradeLogDates = () =>
  api.get<string[]>("/trade-logs/dates").then(r => r.data);

export const fetchTradeSessions = (date: string) =>
  api.get<TradeSession[]>("/trade-logs/sessions", { params: { date } }).then(r => r.data);

export const fetchTradeTimeline = (tradeId: string) =>
  api.get<TradeEventLog[]>(`/trade-logs/${tradeId}`).then(r => r.data);

// ── Trade Chart ──────────────────────────────────────────────────────────────

export const fetchTradeChart = (tradeId: string) =>
  api.get<TradeChartData>(`/trade-chart/${tradeId}`).then(r => r.data);

export const findTradeBySymbol = (symbol: string, at?: string) => {
  // `at` (optional) is an ISO timestamp — when provided, the backend returns
  // the entry closest-but-≤ that time, which lets the P&L day-view resolve
  // the correct trade_id for rows that share a symbol with other trades on
  // the same day.
  const params = at ? `?at=${encodeURIComponent(at)}` : "";
  return api
    .get<{ trade_id: string | null; symbol: string }>(`/trade-chart/by-symbol/${encodeURIComponent(symbol)}${params}`)
    .then(r => r.data);
};
